import dayjs, { Dayjs } from 'dayjs';
import { ClickAwayListener } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LocationHelperTexts } from '../../types/autocomplete';
import { redirectToCategories } from '../../utils/router';
import { RootState, useAppSelector } from '../../redux/store';
import { setSiteLevelEndDate, setSiteLevelStartDate } from '../../redux/slice/cartSlice';
import { Subject } from 'rxjs';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import {
  Value,
  SelectorContent,
  DateSelector,
  Label,
  LocationSelector,
  SubmitButtonInSelectorBar,
  Root,
  SelectorBar,
  SelectorDropdown,
  Form,
  FormItem,
  LocationAutocomplete,
  HelperText,
  DateRangePicker,
  SearchIcon,
  SubmitButtoninSelectorDropdown,
  FormLabel,
  CloseButton,
  CloseIcon,
} from './LocationAndDateSelectorStyle';

const focusInput = new Subject<boolean>();

enum ActiveSelector {
  Location = 'Location',
  Date = 'Date',
  None = 'None',
}

function LocationAndDateSelector() {
  const [activeSelector, setActiveSelector] = useState<ActiveSelector>(ActiveSelector.None);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [displayDate, setDisplayDate] = useState<string | undefined>(undefined);

  const siteLevelDate = useAppSelector((state: RootState) => state.cart.siteLevelDate);
  const locationState = useAppSelector((state: RootState) => state.location);

  const minStartDate = dayjs.utc().local().add(1, 'day');

  const dispatch = useDispatch();

  const locationHelperTexts: LocationHelperTexts = {
    default: {
      element: (
        <HelperText>
          <FormattedMessage id={'generic.locationHelperTexts.default'} />
        </HelperText>
      ),
    },
    invalidInput: {
      element: (
        <HelperText hasError absolutePosition>
          <FormattedMessage id='generic.locationHelperTexts.invalidLocation' />
        </HelperText>
      ),
      position: 'absolute',
    },
  };

  const closeDropdown = () => {
    setActiveSelector(ActiveSelector.None);
  };

  const handleSelectorClick = (selector: ActiveSelector) => {
    setActiveSelector(selector);
  };

  const getDisplayDate = (start: number | undefined, end: number | undefined) => {
    if (start && end) {
      const startDay = dayjs.unix(start).format('D');
      const startMonth = dayjs.unix(start).format('MMM');
      const endDay = dayjs.unix(end).format('D');
      const endMonth = dayjs.unix(end).format('MMM');
      return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
    }

    return;
  };

  const handleStartDateChange = (date: Dayjs) => {
    dispatch(setSiteLevelStartDate(date.unix()));
  };

  const handleEndDateChange = (date: Dayjs) => {
    dispatch(setSiteLevelEndDate(date.unix()));
  };

  useEffect(() => {
    const start = siteLevelDate?.start ? dayjs.unix(siteLevelDate.start) : null;
    const end = siteLevelDate?.end ? dayjs.unix(siteLevelDate.end) : null;
    setStartDate(start);
    setEndDate(end);

    const dateString = getDisplayDate(siteLevelDate?.start, siteLevelDate?.end);
    setDisplayDate(dateString);
  }, [siteLevelDate]);

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <Root>
        <SelectorBar>
          <LocationSelector
            onClick={() => handleSelectorClick(ActiveSelector.Location)}
            active={activeSelector === ActiveSelector.Location}
          >
            <SelectorContent>
              <Label>
                <FormattedMessage id={'locationAndDateSelector.locationLabel'} />
              </Label>
              <Value lightColor={!locationState?.userLocation?.address}>
                {locationState?.userLocation?.address ?? (
                  <FormattedMessage id={'locationAndDateSelector.locationDefaultPlaceholder'} />
                )}
              </Value>
            </SelectorContent>
          </LocationSelector>
          <DateSelector
            onClick={() => handleSelectorClick(ActiveSelector.Date)}
            active={activeSelector === ActiveSelector.Date}
          >
            <SelectorContent>
              <Label>
                <FormattedMessage id={'locationAndDateSelector.dateLabel'} />
              </Label>
              <Value>{displayDate ?? '-- --'}</Value>
            </SelectorContent>
          </DateSelector>
          <SubmitButtonInSelectorBar onClick={redirectToCategories}>
            <FormattedMessage id={'locationAndDateSelector.submitButton'} />
          </SubmitButtonInSelectorBar>
        </SelectorBar>
        {activeSelector !== ActiveSelector.None && (
          <SelectorDropdown>
            <CloseButton onClick={closeDropdown}>
              <CloseIcon></CloseIcon>
            </CloseButton>
            <Form>
              <FormItem>
                <FormLabel>
                  <FormattedMessage id={'locationAndDateSelector.locationLabel'} />
                </FormLabel>
                <LocationAutocomplete
                  resetCurrentLocationOnClickAway={true}
                  hideStartIcon={true}
                  inputFocusSubject={focusInput}
                  endIcon={<SearchIcon />}
                  helperText={locationHelperTexts}
                  showCurrentLocationSelector
                  active={activeSelector === ActiveSelector.Location}
                />
              </FormItem>
              <FormItem>
                <DateRangePicker
                  minStartDate={minStartDate}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={handleStartDateChange}
                  setEndDate={handleEndDateChange}
                  startDateLabel={
                    <FormLabel>
                      <FormattedMessage id={'locationAndDateSelector.form.startDateLabel'} />
                    </FormLabel>
                  }
                  endDateLabel={
                    <FormLabel>
                      <FormattedMessage id={'locationAndDateSelector.form.endDateLabel'} />
                    </FormLabel>
                  }
                  active={activeSelector === ActiveSelector.Date}
                />
              </FormItem>
              <SubmitButtoninSelectorDropdown onClick={redirectToCategories}>
                <FormattedMessage id={'locationAndDateSelector.submitButton'} />
              </SubmitButtoninSelectorDropdown>
            </Form>
          </SelectorDropdown>
        )}
      </Root>
    </ClickAwayListener>
  );
}

export default LocationAndDateSelector;
